import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Play, Pause, SkipForward, SkipBack, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import RosaryBeads from "@/components/RosaryBeads";

const SET_ACCENTS = {
    joyful: { en: "The Annunciation → the Finding in the Temple", es: "La Anunciación → el Hallazgo en el Templo", la: "Annuntiatio → Inventio in Templo" },
    sorrowful: { en: "The Agony → the Crucifixion", es: "La Agonía → la Crucifixión", la: "Agonia → Crucifixio" },
    glorious: { en: "The Resurrection → the Coronation", es: "La Resurrección → la Coronación", la: "Resurrectio → Coronatio" },
    luminous: { en: "The Baptism → the Institution of the Eucharist", es: "El Bautismo → la Institución de la Eucaristía", la: "Baptismus → Institutio Eucharistiae" },
};

const T = {
    kicker:  { en: "The Holy Rosary", es: "El Santo Rosario", la: "Sanctum Rosarium" },
    subtitle:{
        en: "Pray with us deeply, from the heart, at the pace of a rosary bead. Your voice, if you wish, may join at every \"pray for us.\"",
        es: "Ora con nosotros profundamente, con el corazón, al ritmo de una cuenta del rosario. Tu voz, si lo deseas, puede unirse en cada \"ruega por nosotros\".",
        la: "Ora nobiscum ex intimo corde, ad rhythmum sphaerulae rosarii. Vox tua, si vis, se iungat ad singula \"ora pro nobis\".",
    },
    autoAdvance: { en: "Auto-advance to next bead", es: "Avanzar automáticamente a la siguiente cuenta", la: "Automatice ad sequentem sphaerulam progredi" },
    opening: { en: "Opening Prayers", es: "Oraciones Iniciales", la: "Preces Introductoriae" },
    closing: { en: "Closing Prayers", es: "Oraciones Finales", la: "Preces Conclusivae" },
    decade:  { en: "Decade", es: "Década", la: "Decas" },
    bead:    { en: "Bead", es: "Cuenta", la: "Sphaerula" },
    of:      { en: "of", es: "de", la: "ex" },
    leader:  { en: "leader", es: "guía", la: "praecentor" },
    response:{ en: "response", es: "respuesta", la: "responsum" },
    together:{ en: "together", es: "juntos", la: "una simul" },
    prepNext:{ en: "Preparing the next line…", es: "Preparando la siguiente línea…", la: "Sequens versus paratur…" },
    footnote:{
        en: "A cloned male voice leads the Our Fathers and the first half of each Hail Mary; a reverent female voice answers with \"Holy Mary, Mother of God…\". When your own ElevenLabs voice is uploaded, every line above will speak in your timbre — automatically.",
        es: "Una voz masculina guía los Padrenuestros y la primera mitad de cada Avemaría; una voz femenina reverente responde con \"Santa María, Madre de Dios…\". Cuando se cargue tu voz de ElevenLabs, cada línea sonará con tu timbre — automáticamente.",
        la: "Vox virilis praecinit Pater Noster et primam partem cuiusque Ave Maria; vox mulieris reverens respondet \"Sancta Maria, Mater Dei…\". Cum tua vox in ElevenLabs deponetur, omnis versus in tua voce sonabit — sponte.",
    },
    scripture: { en: "Scripture", es: "Escritura", la: "Sacra Scriptura" },
    fruit:     { en: "Fruit of the Mystery", es: "Fruto del Misterio", la: "Fructus Mysterii" },
    meditation:{ en: "Meditation", es: "Meditación", la: "Meditatio" },
};

const LANGS = [
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
    { code: "la", label: "Latina" },
];

// A tiny silent MP3 (44 bytes) used only to unlock the audio element on the
// user's first tap — required to satisfy browser autoplay policies so the
// real prayer audio can play in the same gesture window.
const SILENT_MP3 = "data:audio/mp3;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbGFncy1JbiDvv71Xd3czAAAAAAAAAAAAAAA=";

const Rosary = () => {
    const [setKey, setSetKey] = useState(null);
    const [sequence, setSequence] = useState([]);
    const [meta, setMeta] = useState(null);
    const [setsMeta, setSetsMeta] = useState(null); // full localized names for all four sets
    const [idx, setIdx] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [lang, setLang] = useState("en");
    const [autoAdvance, setAutoAdvance] = useState(true);
    const [speed, setSpeed] = useState(0.9);  // Reverent default — slower than 1.0
    const [includeLitany, setIncludeLitany] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const audioRef = useRef(null);
    const urlRef = useRef(null);

    // Load all set names + today's default on mount
    useEffect(() => {
        Promise.all([api.get("/rosary/today"), api.get("/rosary/sets")]).then(([today, sets]) => {
            setSetKey(today.data.set_key);
            setSetsMeta(sets.data);
        });
    }, []);

    // Load full sequence when set changes OR when litany toggle changes
    useEffect(() => {
        if (!setKey) return;
        setLoading(true);
        api.get(`/rosary/sequence/${setKey}?litany=${includeLitany}`)
            .then(r => {
                setSequence(r.data.segments);
                setMeta({ name: r.data.name, count: r.data.count });
                // Allow deep-linking to a specific segment via ?idx=N (useful for previewing meditations)
                const params = new URLSearchParams(window.location.search);
                const startIdx = parseInt(params.get("idx") || "0", 10);
                setIdx(Math.max(0, Math.min(startIdx, r.data.segments.length - 1)));
            })
            .finally(() => setLoading(false));
    }, [setKey, includeLitany]);

    // Cleanup blob url on unmount
    useEffect(() => () => { if (urlRef.current) { try { URL.revokeObjectURL(urlRef.current); } catch {} } }, []);

    // Cancel pending fetches on segment change
    const abortRef = useRef(null);
    const loadCurrent = async () => {
        if (!sequence[idx]) return null;
        const seg = sequence[idx];
        const text = seg.text[lang] || seg.text.en;
        setLoading(true);
        setError(false);
        if (abortRef.current) abortRef.current.abort();
        const ctl = new AbortController();
        abortRef.current = ctl;
        try {
            const res = await api.post("/tts/segment",
                { text, role: seg.role, language: lang },
                { responseType: "blob", signal: ctl.signal });
            if (ctl.signal.aborted) return null;
            if (!res.data || (res.data.type && !res.data.type.startsWith("audio/"))) throw new Error("not-audio");
            if (urlRef.current) { try { URL.revokeObjectURL(urlRef.current); } catch {} }
            const url = URL.createObjectURL(res.data);
            urlRef.current = url;
            if (audioRef.current) audioRef.current.src = url;
            return url;
        } catch (e) {
            if (e.name === "CanceledError" || e.name === "AbortError") return null;
            setError(true);
            toast.error("Voice paused", { description: "Try again in a moment." });
            return null;
        } finally { setLoading(false); }
    };

    // Auto-advance: when idx or lang changes AND we're already playing,
    // fetch + play the new segment. This effect no longer fires on `playing`
    // going true — the click handler does that itself (preserving user gesture).
    const isFirstAutoRun = useRef(true);
    useEffect(() => {
        if (isFirstAutoRun.current) { isFirstAutoRun.current = false; return; }
        if (!playing) return;
        (async () => {
            const url = await loadCurrent();
            if (url && audioRef.current) {
                try { await audioRef.current.play(); }
                catch (err) { if (err.name !== "AbortError") setPlaying(false); }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx, lang]);

    // PRE-LOAD current segment when idx/lang changes even if NOT playing —
    // this is the fix for the "have to tap play multiple times" bug.
    // By the time the user taps Play, the audio src is already set and ready
    // to fire in the same gesture window. Also warms the audio element so
    // subsequent transitions (between segments) don't stall.
    useEffect(() => {
        if (playing) return;  // active-play effect handles it
        // Fire-and-forget pre-fetch
        (async () => { try { await loadCurrent(); } catch {} })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx, lang]);

    const onEnded = () => {
        if (autoAdvance && idx < sequence.length - 1) setIdx(idx + 1);
        else setPlaying(false);
    };

    /**
     * FIX for the double-tap-play bug: do the fetch + audio.play() inside the
     * click handler itself so the call stays inside the user gesture window.
     * The previous code deferred play() to a useEffect, which ran on the next
     * React tick — by then the browser no longer counted it as a user gesture
     * and rejected the first play() call. The second tap then succeeded only
     * because the <audio> element already had a src by that point.
     */
    const toggle = async () => {
        if (!sequence.length || !audioRef.current) return;
        if (playing) {
            try { audioRef.current.pause(); } catch {}
            setPlaying(false);
            return;
        }
        setPlaying(true);
        // If audio.src isn't set yet (e.g. pre-fetch still in flight), load now.
        // Otherwise, just play the already-loaded src for INSTANT playback.
        try {
            if (!audioRef.current.src || audioRef.current.src === "" || audioRef.current.src === SILENT_MP3) {
                const url = await loadCurrent();
                if (!url || !audioRef.current) { setPlaying(false); return; }
                audioRef.current.src = url;
            }
            audioRef.current.playbackRate = speed;
            await audioRef.current.play();
        } catch (err) {
            setPlaying(false);
            if (err.name !== "AbortError") {
                toast.error("Tap play once more", { description: "The browser needed a moment — one more tap and he begins." });
            }
        }
    };

    const next = () => { if (idx < sequence.length - 1) setIdx(idx + 1); };
    const prev = () => { if (idx > 0) setIdx(idx - 1); };
    const restart = () => { setIdx(0); setPlaying(false); if (audioRef.current) audioRef.current.pause(); };

    const seg = sequence[idx];
    const text = seg ? (seg.text[lang] || seg.text.en) : "";

    // Track the current mystery's meditation — the announce segment carries it,
    // and it should persist through the whole decade (announce + Our Father + 10 Hail Marys + Glory Be + Fatima).
    const currentMeditation = useMemo(() => {
        if (!seg || seg.phase !== "decade" || !seg.decade) return null;
        // Walk backward from current idx to find the most recent segment in the same decade that has meditation
        for (let i = idx; i >= 0; i--) {
            const s = sequence[i];
            if (!s) continue;
            if (s.phase !== "decade" || s.decade !== seg.decade) break;  // left the decade
            if (s.meditation) return s.meditation;
        }
        return null;
    }, [idx, sequence, seg]);

    // Progress label
    const progress = useMemo(() => {
        if (!seg) return "";
        if (seg.phase === "opening") return T.opening[lang];
        if (seg.phase === "closing") return T.closing[lang];
        if (seg.phase === "decade") {
            let msg = `${T.decade[lang]} ${seg.decade} ${T.of[lang]} 5`;
            if (seg.bead) msg += ` · ${T.bead[lang]} ${seg.bead} ${T.of[lang]} 10`;
            return msg;
        }
        return "";
    }, [seg, lang]);

    const decadesDone = useMemo(() => {
        if (!seg) return 0;
        return seg.phase === "closing" ? 5 : (seg.decade ? seg.decade - 1 : 0);
    }, [seg]);

    return (
        <div className="relative" data-testid="rosary-page">
            <section className="relative border-b border-border overflow-hidden">
                <div className="absolute inset-0 -z-0">
                    <img src="https://images.unsplash.com/photo-1509909756405-be0199881695?w=1600&q=80" alt="" className="w-full h-full object-cover opacity-20" />
                    <div className="absolute inset-0 bg-background/70" />
                </div>
                <div className="relative max-w-4xl mx-auto px-6 py-16 text-center">
                    <div className="small-caps text-xs text-muted-foreground mb-3" data-testid="rosary-kicker">{T.kicker[lang]}</div>
                    <h1 className="font-serif text-4xl md:text-5xl leading-tight" data-testid="rosary-title">{meta?.name?.[lang] || meta?.name?.en || T.kicker[lang]}</h1>
                    <p className="mt-5 text-muted-foreground italic max-w-xl mx-auto">{T.subtitle[lang]}</p>
                </div>
            </section>

            <div className="max-w-4xl mx-auto px-6 md:px-10 py-12">
                {/* SET PICKER */}
                <div className="flex items-center justify-center gap-2 flex-wrap mb-8" data-testid="mystery-picker">
                    {["joyful","sorrowful","glorious","luminous"].map(k => (
                        <button key={k} onClick={() => { setSetKey(k); }} data-testid={`set-${k}`}
                            className={`px-4 py-3 text-xs small-caps border transition-colors ${setKey===k ? 'border-liturgy accent-liturgy bg-liturgy/5' : 'border-border text-muted-foreground hover:border-liturgy'}`}>
                            <div className="font-semibold">{setsMeta?.[k]?.name?.[lang] || setsMeta?.[k]?.name?.en || k}</div>
                            <div className="mt-1 normal-case text-[10px] tracking-normal italic text-muted-foreground">{SET_ACCENTS[k][lang]}</div>
                        </button>
                    ))}
                </div>

                {/* LANGUAGE + AUTO-ADVANCE + LITANY */}
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <div className="flex items-center gap-1" data-testid="lang-switcher">
                        {LANGS.map(l => (
                            <button key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-${l.code}`}
                                className={`px-3 py-1.5 text-xs small-caps border ${lang===l.code ? 'border-liturgy accent-liturgy' : 'border-border text-muted-foreground hover:accent-liturgy'}`}>
                                {l.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-5 flex-wrap">
                        <label className="text-xs text-muted-foreground flex items-center gap-2 cursor-pointer" data-testid="toggle-litany-label">
                            <input type="checkbox" checked={includeLitany} onChange={e => setIncludeLitany(e.target.checked)} data-testid="toggle-litany" />
                            <span>Include Litany of Loreto</span>
                        </label>
                        <label className="text-xs text-muted-foreground flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={autoAdvance} onChange={e => setAutoAdvance(e.target.checked)} data-testid="auto-advance" />
                            <span>{T.autoAdvance[lang]}</span>
                        </label>
                    </div>
                </div>

                {/* REVERENT PACE PICKER */}
                <div className="flex items-center justify-center gap-2 mb-6 flex-wrap" data-testid="pace-picker">
                    <span className="small-caps text-[10px] text-muted-foreground mr-1">Pace</span>
                    {[
                        { v: 0.75, label: "Slower" },
                        { v: 0.9,  label: "Reverent" },
                        { v: 1.0,  label: "Steady" },
                        { v: 1.15, label: "Brisker" },
                    ].map(p => (
                        <button key={p.v} onClick={() => { setSpeed(p.v); if (audioRef.current) audioRef.current.playbackRate = p.v; }}
                            data-testid={`pace-${p.v}`}
                            className={`px-3 py-1 text-[10px] small-caps border transition-colors ${speed===p.v ? 'border-liturgy accent-liturgy bg-liturgy/5' : 'border-border text-muted-foreground hover:border-liturgy'}`}>
                            {p.label} · {p.v}×
                        </button>
                    ))}
                </div>

                {/* MEDITATION PANEL — sacred art + scripture prominently displayed ABOVE the prayer card */}
                {currentMeditation && (
                    <div className="mb-10 grid md:grid-cols-2 gap-8 items-start" data-testid="meditation-panel">
                        <div className="relative">
                            <div className="aspect-[4/5] overflow-hidden bg-parchment-100 shadow-md rounded-sm">
                                <img
                                    src={currentMeditation.art.url}
                                    alt={currentMeditation.art.title}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.classList.add('bg-liturgy/5'); e.currentTarget.parentElement.innerHTML = '<div class=\"w-full h-full flex flex-col items-center justify-center text-muted-foreground italic p-6 text-center\"><div class=\"text-xs small-caps mb-2 accent-liturgy\">Sacred Art</div><div class=\"text-sm\">' + currentMeditation.art.title + '</div><div class=\"text-xs mt-1\">' + currentMeditation.art.artist + '</div></div>'; }}
                                />
                            </div>
                            <div className="mt-3 text-xs text-muted-foreground italic text-center">
                                <span className="font-serif not-italic">{currentMeditation.art.title}</span>
                                {" — "}
                                <span>{currentMeditation.art.artist}</span>
                                <span className="ml-1">({currentMeditation.art.year})</span>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <div className="small-caps text-[10px] text-muted-foreground mb-2">{T.meditation[lang]}</div>
                                <h3 className="font-serif text-3xl md:text-4xl leading-tight">
                                    {currentMeditation.mystery_name[lang] || currentMeditation.mystery_name.en}
                                </h3>
                                {lang !== "la" && (
                                    <div className="mt-1 text-sm italic text-muted-foreground">
                                        {currentMeditation.mystery_name.la}
                                    </div>
                                )}
                            </div>

                            <div>
                                <div className="small-caps text-[10px] accent-liturgy mb-2">{T.scripture[lang]} · {currentMeditation.scripture_ref[lang] || currentMeditation.scripture_ref.en}</div>
                                <p className="font-body text-base leading-relaxed text-foreground/90 italic">
                                    {currentMeditation.passages[lang] || currentMeditation.passages.en}
                                </p>
                            </div>

                            <div>
                                <div className="small-caps text-[10px] text-muted-foreground mb-1">{T.fruit[lang]}</div>
                                <div className="font-serif text-lg accent-liturgy">
                                    {currentMeditation.fruit[lang] || currentMeditation.fruit.en}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* MAIN PRAYER CARD + BEADS SIDEBAR */}
                <div className="grid md:grid-cols-[1fr_260px] gap-6 items-start">
                <div className="missal-card p-8 md:p-14 min-h-[420px] flex flex-col" data-testid="rosary-card">
                    <div className="text-center small-caps text-[11px] accent-liturgy" data-testid="rosary-progress">
                        {progress}
                        {seg && (
                            <span className="ml-3 text-muted-foreground normal-case text-[10px] tracking-normal">· {(seg.label?.[lang] || seg.label?.en || seg.label)} · {seg.role === 'response' ? T.response[lang] : seg.role === 'both' ? T.together[lang] : T.leader[lang]}</span>
                        )}
                    </div>

                    <div className="flex-1 flex items-center justify-center py-8">
                        {loading && !text ? (
                            <div className="text-muted-foreground italic animate-pulse">{T.prepNext[lang]}</div>
                        ) : (
                            <p className={`font-body text-center text-lg md:text-xl leading-loose max-w-2xl ${seg?.role === 'response' ? 'italic accent-liturgy' : ''}`} data-testid="rosary-text">
                                {text}
                            </p>
                        )}
                    </div>

                    {/* DECADE DOTS */}
                    <div className="flex items-center justify-center gap-2 mb-8">
                        {[1,2,3,4,5].map(n => (
                            <span key={n} className={`w-2 h-2 rounded-full transition-colors ${decadesDone >= n ? 'bg-liturgy' : (seg?.decade === n ? 'bg-liturgy/60 animate-pulse' : 'bg-border')}`} />
                        ))}
                    </div>

                    {/* CONTROLS */}
                    <div className="flex items-center justify-center gap-3" data-testid="rosary-controls">
                        <button onClick={prev} disabled={idx === 0} data-testid="rosary-prev"
                            className="w-11 h-11 border border-border flex items-center justify-center hover:border-liturgy hover:accent-liturgy disabled:opacity-30">
                            <SkipBack size={16} />
                        </button>
                        <button onClick={toggle} data-testid="rosary-toggle"
                            className="w-16 h-16 rounded-full bg-liturgy text-white flex items-center justify-center hover:scale-105 transition-transform duration-500">
                            {loading && playing ? <span className="text-xs">…</span> : playing ? <Pause size={22} /> : <Play size={22} className="ml-1" />}
                        </button>
                        <button onClick={next} disabled={idx >= sequence.length - 1} data-testid="rosary-next"
                            className="w-11 h-11 border border-border flex items-center justify-center hover:border-liturgy hover:accent-liturgy disabled:opacity-30">
                            <SkipForward size={16} />
                        </button>
                        <button onClick={restart} title="Start over" data-testid="rosary-restart"
                            className="w-11 h-11 border border-border flex items-center justify-center hover:border-liturgy hover:accent-liturgy ml-3">
                            <RotateCcw size={14} />
                        </button>
                    </div>

                    <div className="mt-6 text-center text-xs text-muted-foreground">
                        {sequence.length > 0 && <span>{idx + 1} of {sequence.length}</span>}
                    </div>
                </div>

                {/* BEADS SIDEBAR */}
                <aside className="md:sticky md:top-24 hidden md:block" data-testid="rosary-beads-aside">
                    <div className="missal-card p-5">
                        <div className="small-caps text-[10px] text-muted-foreground text-center mb-3 tracking-widest">The Rosary</div>
                        <RosaryBeads sequence={sequence} idx={idx} />
                    </div>
                </aside>
                </div>

                {/* MOBILE BEADS (below card) */}
                <div className="md:hidden mt-6" data-testid="rosary-beads-mobile">
                    <div className="missal-card p-5">
                        <RosaryBeads sequence={sequence} idx={idx} />
                    </div>
                </div>

                <p className="mt-10 text-center text-xs text-muted-foreground italic max-w-lg mx-auto">
                    {T.footnote[lang]}
                </p>

                <audio ref={audioRef} onEnded={onEnded} onError={() => { setPlaying(false); setError(true); }} />
            </div>
        </div>
    );
};

export default Rosary;
